import { ChartDataset, ChartOptions, ChartTypeRegistry, DefaultDataPoint } from 'chart.js'
import { store } from '../../store/Store'

type Datastore<TType extends keyof ChartTypeRegistry, TData> = ChartDataset<TType, TData> & { plan: boolean }

const ChartsHelper = {
    loadingColors: () => {
        return [
            '#7F7F7F',
            '#696969',
            '#555555',
            '#4A4A4A',
            '#414141',
            '#383838',
            '#2D2D2D',
            '#1A1A1A'
        ]
    },
    colorsFromTheme: () => {
        return [
            '#0575e6',
            '#0558c7',
            '#063ca8',
            '#042089',
            '#00006a',
            '#03dac5',
            '#00c7db',
            '#00b0ee',
            '#0095f4'
        ]
    },
    distinctColors: () => {
        return [
            '#003f5c',
            '#2f4b7c',
            '#665191',
            '#a05195',
            '#d45087',
            '#f95d6a',
            '#ff7c43',
            '#ffa600'
        ]
    },
    applyColors: <TType extends keyof ChartTypeRegistry, TData>(datastores: Datastore<TType, TData>[], isLoading?: boolean) => {
        if (datastores) {
            const colors = isLoading ? ChartsHelper.loadingColors() : ChartsHelper.colorsFromTheme()

            for (let i = 0; i < datastores.length; i++) {
                const datastore = datastores[i]
                const color = colors[i % 8]

                if (!(datastore as any).backgroundColor)
                    (datastore as any).backgroundColor = color
                if (!datastore.borderColor)
                    datastore.borderColor = color
            }
        }
    },
    applyColorsToPieChart: <TType extends keyof ChartTypeRegistry, TData>(datasets: ChartDataset<TType, TData>[], isLoading?: boolean) => {
        const colors = isLoading ? ChartsHelper.loadingColors() : ChartsHelper.colorsFromTheme()

        for (let i = 0; i < datasets?.length; i++) {
            const dataset = datasets[i];
            (dataset as any).backgroundColor = (dataset.data as Array<number>).map((_, j) => colors[j % 8])
            dataset.borderColor = (dataset.data as Array<number>).map((_, j) => colors[j % 8])
        }
    },
    applyMode: (options: ChartOptions) => {
        if (!options) return

        const mode = store.getState().app.mode
        const fontColor = mode === 'dark' ? 'rgba(255,255,255,.87)' : 'rgba(0, 0, 0, 0.87)'

        options.scales ??= {}
        options.plugins ??= {}

        // Apply default theme colors only if the axis is being displayed
        if (options.scales.x?.display !== false) {
            options.scales.x ??= {}
            options.scales.x.ticks ??= {}

            // Set the color if its not defined through chart options
            if (!options.scales.x.ticks.color) {
                options.scales.x.ticks.color = fontColor
            }

            // @ts-expect-error The typing that we import is not correct with the latest release
            options.scales.x.border ??= {}
            // @ts-expect-error The typing that we import is not correct with the latest release
            if (!options.scales.x.border.color) {
            // @ts-expect-error The typing that we import is not correct with the latest release
                options.scales.x.border.color = fontColor
            }
        }

        const applyStylesToY = (scaleKey: 'y' | 'y2') => {
            const scale = options.scales?.[scaleKey]

            // Check if Y axis is set and visible
            if (scale && scale.display !== false) {
                scale.ticks ??= {}

                if (!scale.ticks.color) {
                    scale.ticks.color = fontColor
                }

                if (options.indexAxis !== 'y') {
                // @ts-expect-error The typing that we import is not correct with the latest release
                    scale.border ??= {}
                    // @ts-expect-error The typing that we import is not correct with the latest release
                    if (!scale.border.color) {
                    // @ts-expect-error The typing that we import is not correct with the latest release
                        scale.border.color = fontColor
                    }
                }
            }
        }

        applyStylesToY('y')
        applyStylesToY('y2')

        options.plugins.legend ??= {}
        options.plugins.legend.labels ??= {}

        if (!options.plugins.legend.labels.color) {
            options.plugins.legend.labels.color = fontColor
        }
    }
}

export default ChartsHelper

export type ChartDatasetWithUnit<TType extends keyof ChartTypeRegistry = keyof ChartTypeRegistry, TData = DefaultDataPoint<TType>> =
    ChartDataset<TType, TData> & { unit?: string }

export interface ChartDataWithUnit<TType extends keyof ChartTypeRegistry = keyof ChartTypeRegistry, TData = DefaultDataPoint<TType>, TLabel = unknown> {
    datasets: ChartDatasetWithUnit<TType, TData>[]
    labels?: TLabel[]
}
